import { z } from 'zod';

/**
 * Mirrors `provisionMerchantSchema` in apps/api/src/modules/identity/http/platform-admin.dto.ts - same field names, same bounds -
 * plus `confirmPassword`, the operator's OWN password, which the API's re-authentication guard consumes before the handler runs.
 * The server re-validates independently; this exists so an operator sees the problem before the round trip, never so the server can
 * trust it.
 *
 * NO PASSWORD FIELD FOR THE NEW ACCOUNT, and that is the design rather than an omission: the SERVER generates a provisional password
 * and returns it once. A password typed on this screen would be known to two people and would have to travel to reach the first.
 */
export const provisionMerchantFormSchema = z.object({
  email: z.string().trim().toLowerCase().email('Adresse email invalide.').max(320),
  legalName: z.string().trim().min(2, 'Raison sociale trop courte.').max(200),
  organisationActive: z.boolean(),
  /**
   * The administrator who will manage the shop (its portfolio). Empty for an administrator (the shop joins its own portfolio, decided
   * server side); a superadmin picks one - the database refuses the creation without it. Only a UUID or nothing is accepted here.
   */
  managedBy: z.union([z.literal(''), z.string().uuid()]).optional(),
  /** The language of the merchant's account (their e-mail-free screens follow it): chosen by the operator, defaulting to the screen's own. */
  locale: z.enum(['fr', 'ar', 'en']),
  reason: z.string().trim().min(10, 'Le motif doit contenir au moins 10 caractères.').max(500),
  confirmPassword: z.string().min(1, 'Saisissez votre mot de passe pour confirmer.').max(1024),
});

export type ProvisionMerchantFormValues = z.infer<typeof provisionMerchantFormSchema>;

/** What `POST /admin/organisations` answers (201). `temporaryPassword` is shown once and kept nowhere else. */
export interface ProvisionedMerchant {
  readonly organisationId: string;
  readonly ownerUserId: string;
  readonly status: 'active' | 'pending';
  readonly temporaryPassword: string;
  readonly expiresAt: string;
}
